import React from 'react';
import { User, Building, Users, Mail, LogOut, Settings as SettingsIcon, Edit3, Save, X, Settings2Icon, Key, Link2, Shield, Check } from 'lucide-react';
import { buttonStyles } from '../../utils/colors';
import LogoUpload from '../../components/LogoUpload';
import ApiKeysTab from '../../components/ApiKeysTab';
import IntegrationsTab from '../../components/IntegrationsTab';
import useSettings from '../../hooks/useSettings';

export default function FastestFormSettings() {
  const {
    showDarkModeWarning, darkMode, organizations, orgSettings, userSettings,
    members, pendingInvites, pendingAccessRequests, inviteEmail, loading, isEditingUser, activeTab,
    user, organization,
    setShowDarkModeWarning, setOrgSettings, setUserSettings, setInviteEmail,
    setIsEditingUser, setActiveTab,
    handleDarkModeToggle, confirmDarkMode, handleUserUpdate, handleCancelUserEdit,
    handleOrgUpdate, handleInviteUser, handleRemoveMember, handleCancelInvitation,
    handleReviewAccessRequest,
    switchOrganization, updateOrganization, logout,
  } = useSettings();

  return (
    <>
     {showDarkModeWarning && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50" >
          <div className='absolute inset-0 z-20' onClick={() => setShowDarkModeWarning(false)}></div>
          <div className="bg-white z-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-w-xl w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Dark Mode is experimental</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Dark mode is not fully complete yet. Some pages and features may not display correctly.<br /><br />
              <strong>Note:</strong> Dark mode only changes the design for <u>you</u> as a user. It does <u>not</u> affect your organization or your form respondents.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => setShowDarkModeWarning(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-md bg-brand-600 text-white hover:bg-brand-700"
                onClick={confirmDarkMode}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

    <div className="max-w-6xl mx-auto py-8 px-4 space-y-8">
      {/* Dark Mode Warning Dialog */}

      {/* Header */}
      <div className="flex items-center gap-3">
        <SettingsIcon className="h-8 w-8 text-gray-700 dark:text-gray-200" />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
      </div>      {/* Current Organization Banner */}
  <div className="bg-gradient-to-r from-brand-50 to-brand-100 dark:from-gray-800 dark:to-gray-900 border border-brand-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-brand-200 rounded-full flex items-center justify-center overflow-hidden">
              {organization?.logo_url ? (
                <img
                  src={organization.logo_url}
                  alt={`${organization.name} logo`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Building className="w-6 h-6 text-brand-700" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-brand-900 dark:text-brand-200">
                {organization?.name || 'No Organization'}
              </h2>
              <p className="text-brand-700 dark:text-brand-300">
                Currently managing • {members.length} member{members.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          {organizations.length > 1 && (
            <div className="text-right">
              <p className="text-sm text-brand-700 mb-2">Switch Organization</p>
              <select
                value={organization?.id || ''}
                onChange={(e) => switchOrganization(e.target.value)}
                className="block px-3 py-2 border border-brand-300 dark:border-brand-700 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 dark:text-gray-100"
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Tabbed Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Left Sidebar - Navigation */}
        <div className="lg:col-span-1">
          <nav className="space-y-2 bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('account')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                activeTab === 'account'
                  ? 'bg-brand-100 dark:bg-brand-800 text-brand-700 dark:text-brand-200 border border-brand-200 dark:border-brand-600'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
              }`}
            >
              <User className="w-5 h-5" />
              <span className="font-medium">Account Settings</span>
            </button>
            <button
              onClick={() => setActiveTab('organization')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                activeTab === 'organization'
                  ? 'bg-brand-100 dark:bg-brand-800 text-brand-700 dark:text-brand-200 border border-brand-200 dark:border-brand-600'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
              }`}
            >
              <Building className="w-5 h-5" />
              <span className="font-medium">Organization</span>
            </button>
            <button
              onClick={() => setActiveTab('members')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                activeTab === 'members'
                  ? 'bg-brand-100 dark:bg-brand-800 text-brand-700 dark:text-brand-200 border border-brand-200 dark:border-brand-600'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
              }`}
            >
              <Users className="w-5 h-5" />
              <span className="font-medium">Members & Invites</span>
            </button>
            <button
              onClick={() => setActiveTab('api-keys')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                activeTab === 'api-keys'
                  ? 'bg-brand-100 dark:bg-brand-800 text-brand-700 dark:text-brand-200 border border-brand-200 dark:border-brand-600'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
              }`}
            >
              <Key className="w-5 h-5" />
              <span className="font-medium">API Keys</span>
            </button>
            <button
              onClick={() => setActiveTab('integrations')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                activeTab === 'integrations'
                  ? 'bg-brand-100 dark:bg-brand-800 text-brand-700 dark:text-brand-200 border border-brand-200 dark:border-brand-600'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
              }`}
            >
              <Link2 className="w-5 h-5" />
              <span className="font-medium">Integrations</span>
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {activeTab === 'account' && (
            <div className="space-y-6">
              {/* Account Settings Header */}
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Account Settings</h2>
                <p className="text-gray-600 dark:text-gray-300">Manage your personal account information and preferences.</p>
              </div>


              {/* User Profile Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Profile Information</h3>
                    </div>
                    {!isEditingUser && (
                      <button
                        onClick={() => setIsEditingUser(true)}
                        className={`${buttonStyles.secondary} flex items-center gap-2`}
                      >
                        <Edit3 className="h-3 w-3" />
                        Edit
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  {isEditingUser ? (
                    <form onSubmit={handleUserUpdate} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email</label>
                          <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
                            {user?.email}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Email cannot be changed</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Username</label>
                          <input
                            type="text"
                            value={userSettings.username}
                            onChange={(e) => setUserSettings({ ...userSettings, username: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                            placeholder="Enter username"
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Full Name</label>
                          <input
                            type="text"
                            value={userSettings.full_name}
                            onChange={(e) => setUserSettings({ ...userSettings, full_name: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                            placeholder="Enter your full name"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <button
                          type="button"
                          onClick={handleCancelUserEdit}
                          className={`${buttonStyles.secondary} flex items-center gap-2`}
                        >
                          <X className="h-3 w-3" />
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className={`${buttonStyles.primary} ${buttonStyles.small} flex items-center gap-1`}
                        >
                          <Save className="h-3 w-3" />
                          {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email</label>
                        <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
                          {user?.email}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Username</label>
                        <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
                          {user?.username}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Full Name</label>
                        <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
                          {user?.full_name || 'Not set'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

               {/* Dark Mode Toggle */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Settings2Icon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Preferences</h3>
                    </div>
                  </div>
                </div>
                <div className="p-6 flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-200">Dark Mode</span>
                  <button
                    type="button"
                    aria-pressed={darkMode}
                    onClick={handleDarkModeToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 border border-gray-300 dark:border-gray-700 ${darkMode ? 'bg-brand-600' : 'bg-gray-200'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-900 shadow transition-transform ${darkMode ? 'translate-x-5' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
              </div>

              {/* Sign Out Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <LogOut className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Account Actions</h3>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Sign Out</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Sign out of your account on this device</p>
                    </div>
                    <button
                      onClick={logout}
                      className={buttonStyles.danger}
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'organization' && (
            <div className="space-y-6">
              {/* Organization Settings Header */}
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Organization Settings</h2>
                <p className="text-gray-600 dark:text-gray-300">Manage settings for <strong>{organization?.name}</strong>.</p>
              </div>

              {/* Organization Details Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Building className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Organization Details</h3>
                  </div>
                </div>
                <div className="p-6">
                  <form onSubmit={handleOrgUpdate} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Organization Name</label>
                      <input
                        type="text"
                        value={orgSettings.name}
                        onChange={(e) => setOrgSettings({ ...orgSettings, name: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        placeholder="Enter organization name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Description</label>
                      <textarea
                        value={orgSettings.description}
                        onChange={(e) => setOrgSettings({ ...orgSettings, description: e.target.value })}
                        rows={3}
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        placeholder="Enter organization description"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className={buttonStyles.secondary}
                      >
                        {loading ? 'Updating...' : 'Update Organization'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Logo Upload Card */}
              <LogoUpload
                organization={organization}
                onLogoUpdate={(updatedOrg) => updateOrganization(updatedOrg)}
                isOwner={organization?.owner_id === user?.id}
              />
            </div>
          )}

          {activeTab === 'members' && (
            <div className="space-y-6">
              {/* Members & Invites Header */}
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Members & Invites</h2>
                <p className="text-gray-600 dark:text-gray-300">Manage team members for <strong>{organization?.name}</strong>.</p>
              </div>

              {/* Invite Users Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Invite New Members</h3>
                  </div>
                </div>
                <div className="p-6">
                  <form onSubmit={handleInviteUser} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email Address</label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="user@example.com"
                        required
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading || !inviteEmail.trim()}
                      className={`${buttonStyles.primary} ${buttonStyles.full}`}
                    >
                      {loading ? 'Sending Invite...' : 'Send Invitation'}
                    </button>
                  </form>
                </div>
              </div>

              {/* Organization Members Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Current Members ({members.length})
                    </h3>
                  </div>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {members.length === 0 ? (
                    <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                      No members found
                    </div>
                  ) : (
                    members.map((member) => (
                      <div key={member.id} className="p-4 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-brand-100 dark:bg-brand-700 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-brand-600 dark:text-brand-200" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {member.full_name || member.username}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {member.email}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            organization?.owner_id === member.id
                              ? 'bg-amber-100 dark:bg-amber-700 text-amber-800 dark:text-amber-100'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                          }`}>
                            {organization?.owner_id === member.id ? 'Owner' : 'Member'}
                          </span>
                          {organization?.owner_id === user?.id && organization?.owner_id !== member.id && (
                            <button
                              onClick={() => handleRemoveMember(member.id)}
                              className="text-rose-600 dark:text-rose-400 hover:text-rose-900 dark:hover:text-rose-200 text-sm transition-colors"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Pending Invitations Card */}
              {pendingInvites.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Pending Invitations ({pendingInvites.length})
                      </h3>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pendingInvites.map((invite) => (
                      <div key={invite.id} className="p-4 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-amber-100 dark:bg-amber-700 rounded-full flex items-center justify-center">
                              <Mail className="w-4 h-4 text-amber-600 dark:text-amber-100" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {invite.email}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Invited by {invite.invited_by?.full_name || invite.invited_by?.username} •
                                Expires {new Date(invite.expires_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 text-xs rounded-full bg-amber-100 dark:bg-amber-700 text-amber-800 dark:text-amber-100">
                            Pending
                          </span>
                          {organization?.owner_id === user?.id && (
                            <button
                              onClick={() => handleCancelInvitation(invite.id)}
                              className="text-rose-600 dark:text-rose-400 hover:text-rose-900 dark:hover:text-rose-200 text-sm transition-colors"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Staff Access Requests Card — only shown to org owner when there are pending requests */}
              {organization?.owner_id === user?.id && pendingAccessRequests.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-indigo-200 dark:border-indigo-700 overflow-hidden">
                  <div className="px-6 py-4 bg-indigo-50 dark:bg-indigo-900/30 border-b border-indigo-200 dark:border-indigo-700">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Staff Access Requests ({pendingAccessRequests.length})
                      </h3>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      A member of our staff is requesting temporary access to help with your organisation.
                    </p>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pendingAccessRequests.map((req) => (
                      <div key={req.id} className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="w-9 h-9 bg-indigo-100 dark:bg-indigo-800 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Shield className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {req.superadmin_name || req.superadmin_email}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {req.superadmin_email} • Requesting {req.requested_days} days access •{' '}
                                {new Date(req.created_at).toLocaleDateString()}
                              </p>
                              {req.message && (
                                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 p-2.5 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
                                  "{req.message}"
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleReviewAccessRequest(req.id, 'deny')}
                              className="px-3 py-1.5 text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors"
                            >
                              Deny
                            </button>
                            <button
                              onClick={() => handleReviewAccessRequest(req.id, 'approve')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                            >
                              <Check className="w-3.5 h-3.5" />
                              Approve
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'api-keys' && (
            <ApiKeysTab organization={organization} />
          )}

          {activeTab === 'integrations' && (
            <IntegrationsTab />
          )}
        </div>
      </div>
    </div>
  </>);
}
